import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import { adminService } from '../../services/admin';
import {
  RegistrationPetitionDetail,
  RegistrationStatus,
  RegistrationReviewStatus,
  RegistrationReviewStatusName,
} from '../../types/admin';
import { normalizeRegistrationStatus } from '../../services/applicationStatus';
import {
  ContactPreference,
  Gender,
  MembershipType,
  ProfessionalPath,
  StudyDegreeType,
  Title,
  WorkSector,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
  type Study,
} from '../../types/member';
import {
  AdministrativeStatus,
  InstitutionContactPreference,
  InstitutionMembershipCategory,
  type InstitutionDto,
} from '../../types/institution';
import type { BilingualAddress, BilingualName, BilingualText } from '../../types/common';
import type { InstitutionCommittee } from '../../types/committee';
import { formatImportProblems } from '../../utils/importProblems';
import { formatInstitutionTypeLabel } from '../../data/icomInstitutionTypes';
import { tryNormalizeEnumValue } from '../../utils/enumNormalize';
import { Container, Stack, Flex } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text, Label } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { formatAthensDate, formatAthensDateTime } from '../../utils/athensTime';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const Page = styled(Container)`
  max-width: 960px;
`;

const Section = styled(Card)`
  padding: ${spacing[6]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[4]};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const FieldLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
`;

const FieldValue = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
  word-break: break-word;
`;

const StatusBadge = styled.span<{ $status: number }>`
  display: inline-flex;
  align-items: center;
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borders.radius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  background: ${props => {
    if (props.$status === 2) return `${props.theme.colors.success}18`;
    if (props.$status === 3) return `${props.theme.colors.error}18`;
    if (props.$status === 4) return `${props.theme.colors.info ?? props.theme.colors.primary}18`;
    return `${props.theme.colors.warning}18`;
  }};
  color: ${props => {
    if (props.$status === 2) return props.theme.colors.success;
    if (props.$status === 3) return props.theme.colors.error;
    if (props.$status === 4) return props.theme.colors.info ?? props.theme.colors.primary;
    return props.theme.colors.warning;
  }};
`;

const DocRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[3]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  > *:first-child {
    flex: 1 1 12rem;
    min-width: 0;
  }

  > button {
    flex-shrink: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FeedbackBox = styled.div`
  padding: ${spacing[4]};
  border-radius: ${borders.radius.md};
  background: ${props => `${props.theme.colors.warning}14`};
  border-left: 3px solid ${props => props.theme.colors.warning};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${spacing[3]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  resize: vertical;
`;

const SubHeading = styled(Text)`
  margin: ${spacing[4]} 0 ${spacing[2]};
  font-weight: ${typography.fontWeight.semibold};
`;

const EMPTY = '—';

function documentTypeLabel(type: number | string, t: (k: string) => string): string {
  const n = typeof type === 'string' ? type : String(type);
  if (n === '7' || n === 'CV') return t('petitions.docType.cv');
  if (n === '8' || n === 'ProfessionalActivityDocuments') return t('petitions.docType.professional');
  if (n === '9' || n === 'StudentID') return t('petitions.docType.studentId');
  if (n === '3' || n === 'Other') return t('petitions.docType.institution');
  return String(type);
}

function displayOrDash(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  return String(value);
}

function bilingualText(text: BilingualText | undefined, locale: 'en' | 'gr'): string {
  if (!text) return '';
  return locale === 'gr' ? text.gr || text.en || '' : text.en || text.gr || '';
}

function bilingualName(name: BilingualName | undefined, locale: 'en' | 'gr'): string {
  if (!name) return '';
  const first =
    locale === 'gr'
      ? name.firstNameGr || name.firstNameEn
      : name.firstNameEn || name.firstNameGr;
  const last =
    locale === 'gr' ? name.lastNameGr || name.lastNameEn : name.lastNameEn || name.lastNameGr;
  return [first, last].filter(Boolean).join(' ');
}

function formatAddress(addr: BilingualAddress | undefined, locale: 'en' | 'gr'): string {
  if (!addr) return '';
  const street =
    locale === 'gr' ? addr.addressGr || addr.addressEn : addr.addressEn || addr.addressGr;
  const city = locale === 'gr' ? addr.cityGr || addr.cityEn : addr.cityEn || addr.cityGr;
  const postal =
    locale === 'gr'
      ? addr.postalCodeGr || addr.postalCodeEn
      : addr.postalCodeEn || addr.postalCodeGr;
  const country =
    locale === 'gr' ? addr.countryGr || addr.countryEn : addr.countryEn || addr.countryGr;
  return [street, city, postal, country].filter(Boolean).join(', ');
}

function FieldRow({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value?: string | number | null;
  fullWidth?: boolean;
}) {
  return (
    <Field style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>{displayOrDash(value)}</FieldValue>
    </Field>
  );
}

const AdminRegistrationPetitionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();

  const [detail, setDetail] = useState<RegistrationPetitionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewModal, setReviewModal] = useState<'reject' | 'needsChanges' | null>(null);
  const [reviewMessage, setReviewMessage] = useState('');
  const [confirmApprove, setConfirmApprove] = useState(false);

  const locale: 'en' | 'gr' = i18n.language?.startsWith('gr') ? 'gr' : 'en';

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await adminService.getRegistrationPetitionDetail(id);
      setDetail(data);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('petitions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, showError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const statusNum = useMemo(
    () => normalizeRegistrationStatus(detail?.status),
    [detail?.status]
  );

  const statusLabel = (status: number) => {
    if (status === RegistrationStatus.Approved) return t('petitions.statusApproved');
    if (status === RegistrationStatus.Rejected) return t('petitions.statusRejected');
    if (status === RegistrationStatus.NeedsChanges) return t('petitions.statusNeedsChanges');
    return t('petitions.statusPending');
  };

  const displayName = useMemo(() => {
    if (!detail) return '';
    if (detail.individual) {
      const first = getMemberDisplayFirstName(detail.individual, locale);
      const last = getMemberDisplayLastName(detail.individual, locale);
      return `${first} ${last}`.trim();
    }
    const inst = detail.institution;
    return (
      (locale === 'gr'
        ? inst?.institutionName?.gr ?? inst?.institutionName?.en
        : inst?.institutionName?.en ?? inst?.institutionName?.gr) ?? ''
    );
  }, [detail, locale]);

  const titleLabel = (title?: Title | string | number | null) => {
    const n = tryNormalizeEnumValue(Title, title);
    if (n == null) return '';
    if (n === Title.Mr) return t('register.titleMr');
    if (n === Title.Ms) return t('register.titleMs');
    if (n === Title.Mrs) return t('register.titleMrs');
    if (n === Title.Dr) return t('register.titleDr');
    if (n === Title.Prof) return t('register.titleProf');
    if (n === Title.Other) return t('register.titleOther');
    return '';
  };

  const genderLabel = (gender?: Gender | string | number | null) => {
    const n = tryNormalizeEnumValue(Gender, gender);
    if (n == null) return '';
    if (n === Gender.Female) return t('profile.gender.female');
    if (n === Gender.Male) return t('profile.gender.male');
    if (n === Gender.Other) return t('profile.gender.other');
    if (n === Gender.PreferNotToSay) return t('profile.gender.preferNotToSay');
    return '';
  };

  const membershipTypeLabel = (type?: MembershipType | string | number | null) => {
    const n = tryNormalizeEnumValue(MembershipType, type);
    if (n == null) return '';
    if (n === MembershipType.Regular) return t('membership.regular');
    if (n === MembershipType.Student) return t('membership.student');
    if (n === MembershipType.Retired) return t('membership.retired');
    if (n === MembershipType.Honorary) return t('membership.honorary');
    return '';
  };

  const professionalPathLabel = (path?: ProfessionalPath | string | number | null) => {
    const n = tryNormalizeEnumValue(ProfessionalPath, path);
    if (n == null) return '';
    if (n === ProfessionalPath.ActiveMuseumProfessional) return t('register.professionalPathActive');
    if (n === ProfessionalPath.Student) return t('register.professionalPathStudent');
    if (n === ProfessionalPath.InterestedNonProfessional) return t('register.professionalPathInterested');
    return '';
  };

  const workSectorLabel = (sector?: WorkSector | string | number | null) => {
    const n = tryNormalizeEnumValue(WorkSector, sector);
    if (n == null) return '';
    const keys: Record<number, string> = {
      [WorkSector.Museology]: 'workSector.museology',
      [WorkSector.Education]: 'workSector.education',
      [WorkSector.Documentation]: 'workSector.documentation',
      [WorkSector.Conservation]: 'workSector.conservation',
      [WorkSector.Exhibition]: 'workSector.exhibition',
      [WorkSector.Management]: 'workSector.management',
      [WorkSector.Communication]: 'workSector.communication',
      [WorkSector.Other]: 'workSector.other',
    };
    return keys[n] ? t(keys[n]) : '';
  };

  const contactPreferenceLabel = (pref?: ContactPreference | string | number | null) => {
    const n = tryNormalizeEnumValue(ContactPreference, pref);
    if (n == null) return '';
    if (n === ContactPreference.Personal) return t('register.contactPreferencePersonal');
    if (n === ContactPreference.Professional) return t('register.contactPreferenceProfessional');
    return '';
  };

  const membershipCategoryLabel = (category?: InstitutionMembershipCategory | string | number | null) => {
    const n = tryNormalizeEnumValue(InstitutionMembershipCategory, category);
    if (n == null) return '';
    const labels: Record<InstitutionMembershipCategory, string> = {
      [InstitutionMembershipCategory.ActiveI]: t('register.budgetLessThan30k'),
      [InstitutionMembershipCategory.ActiveII]: t('register.budget30kTo100k'),
      [InstitutionMembershipCategory.ActiveIII]: t('register.budget100kTo1M'),
      [InstitutionMembershipCategory.ActiveIV]: t('register.budget1MTo5M'),
      [InstitutionMembershipCategory.ActiveV]: t('register.budget5MTo10M'),
      [InstitutionMembershipCategory.ActiveVI]: t('register.budgetMoreThan10M'),
      [InstitutionMembershipCategory.Benefactor]: t('register.budgetBenefactor'),
    };
    return labels[n] || '';
  };

  const administrativeStatusLabel = (status?: AdministrativeStatus | string | number | null) => {
    const n = tryNormalizeEnumValue(AdministrativeStatus, status);
    if (n == null) return '';
    if (n === AdministrativeStatus.Public) return t('institution.status.public');
    if (n === AdministrativeStatus.Private) return t('institution.status.private');
    if (n === AdministrativeStatus.Mixed) return t('institution.status.mixed');
    return '';
  };

  const icomContactPrefLabel = (pref?: InstitutionContactPreference | string | number | null) => {
    const n = tryNormalizeEnumValue(InstitutionContactPreference, pref);
    if (n == null) return '';
    if (n === InstitutionContactPreference.Primary) return t('register.contactPreferencePrimary');
    if (n === InstitutionContactPreference.Secondary) return t('register.contactPreferenceSecondary');
    return '';
  };

  const degreeTypeLabel = (degree?: StudyDegreeType | string | number | null) => {
    const n = tryNormalizeEnumValue(StudyDegreeType, degree);
    if (n == null) return '';
    if (n === StudyDegreeType.Bachelors) return t('register.studies.bachelors');
    if (n === StudyDegreeType.Masters) return t('register.studies.masters');
    if (n === StudyDegreeType.PhD) return t('register.studies.phd');
    if (n === StudyDegreeType.Other) return t('register.studies.other');
    return '';
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    return formatAthensDate(value, i18n.language);
  };

  const runReview = async (status: RegistrationReviewStatusName, message?: string) => {
    if (!detail || actionLoading) return;
    setActionLoading(true);
    try {
      await adminService.reviewPetition(detail.id, {
        status,
        rejectionReason: message?.trim() || undefined,
      });
      if (status === RegistrationReviewStatus.Approved) showSuccess(t('petitions.approvedSuccess'));
      else if (status === RegistrationReviewStatus.Rejected) showSuccess(t('petitions.rejectedSuccess'));
      else showSuccess(t('petitions.needsChangesSuccess'));
      setReviewModal(null);
      setReviewMessage('');
      setConfirmApprove(false);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('petitions.reviewFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async (docId: string, fileName: string) => {
    try {
      const blob = await adminService.downloadRegistrationDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('petitions.loadError'));
    }
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner />
      </Page>
    );
  }

  if (!detail) {
    return (
      <Page>
        <Text>{t('petitions.loadError')}</Text>
        <Button variant="outline" onClick={() => navigate('/admin/petitions')}>
          {t('common.back')}
        </Button>
      </Page>
    );
  }

  const canReview =
    statusNum === RegistrationStatus.Pending || statusNum === RegistrationStatus.NeedsChanges;

  const ind = detail.individual;
  const inst = detail.institution;

  const renderIndividualSections = () => {
    if (!ind) return null;

    const studies = (ind.studies ?? []) as Study[];
    const additional = ind.additionalCommittees ?? [];

    return (
      <>
        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('profile.sections.personal')}
          </Heading>
          <Grid>
            <FieldRow label={t('register.titleOption')} value={titleLabel(ind.title)} />
            <FieldRow
              label={t('petitions.nameEn')}
              value={[ind.name?.firstNameEn, ind.name?.lastNameEn].filter(Boolean).join(' ')}
            />
            <FieldRow
              label={t('petitions.nameGr')}
              value={[ind.name?.firstNameGr, ind.name?.lastNameGr].filter(Boolean).join(' ')}
            />
            <FieldRow label={t('profile.genderLabel')} value={genderLabel(ind.gender)} />
            <FieldRow label={t('profile.dateOfBirth')} value={formatDate(ind.dateOfBirth)} />
            <FieldRow label={t('profile.email')} value={ind.email || detail.accountEmail} />
            <FieldRow label={t('petitions.accountEmail')} value={detail.accountEmail} />
            <FieldRow label={t('profile.phone')} value={ind.phone} />
            <FieldRow label={t('profile.mobile')} value={ind.mobile} />
            <FieldRow
              label={t('register.contactPreference')}
              value={contactPreferenceLabel(ind.contactPreference)}
            />
            <FieldRow
              label={t('profile.residentialAddress')}
              value={formatAddress(ind.residential, locale)}
              fullWidth
            />
            {ind.importProblems && (
              <Field style={{ gridColumn: '1 / -1' }}>
                <FieldLabel>{t('admin.excelImport.importProblems')}</FieldLabel>
                <FieldValue style={{ whiteSpace: 'pre-wrap' }}>
                  {formatImportProblems(ind.importProblems, t)}
                </FieldValue>
              </Field>
            )}
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('profile.sections.professional')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('register.professionalPath')}
              value={professionalPathLabel(ind.professionalPath)}
            />
            <FieldRow
              label={t('register.organisationNameEn')}
              value={bilingualText(ind.organisationName, locale)}
            />
            <FieldRow
              label={t('profile.professionalTitle')}
              value={
                ind.professionalTitleDisplay || bilingualText(ind.professionalTitle, locale)
              }
            />
            <FieldRow
              label={t('profile.professionalInstitution')}
              value={
                ind.professionalInstitutionNameDisplay ||
                bilingualText(ind.professionalInstitutionName, locale)
              }
            />
            <FieldRow label={t('profile.workSector')} value={workSectorLabel(ind.workSector)} />
            <FieldRow
              label={t('profile.professionalAddress')}
              value={
                (locale === 'gr'
                  ? ind.professionalAddressGr || ind.professionalAddressEn || ind.professionalAddress
                  : ind.professionalAddressEn ||
                    ind.professionalAddressGr ||
                    ind.professionalAddress) || ''
              }
            />
            <FieldRow
              label={t('profile.professionalCity')}
              value={
                (locale === 'gr'
                  ? ind.professionalCityGr || ind.professionalCityEn || ind.professionalCity
                  : ind.professionalCityEn || ind.professionalCityGr || ind.professionalCity) || ''
              }
            />
            <FieldRow
              label={t('profile.professionalPostalCode')}
              value={ind.professionalPostalCode}
            />
            <FieldRow label={t('profile.professionalEmail')} value={ind.professionalEmail} />
            <FieldRow label={t('profile.professionalPhone')} value={ind.professionalPhone} />
            <FieldRow label={t('register.workplaceWebsite')} value={ind.website} />
            <FieldRow
              label={t('profile.statusFlags')}
              value={
                [
                  ind.isGraduateStudent ? t('profile.isGraduateStudent') : null,
                  ind.isRetired ? t('profile.isRetired') : null,
                  ind.isHonoraryMember ? t('membership.honorary') : null,
                ]
                  .filter(Boolean)
                  .join(', ') || t('profile.noStatusFlags')
              }
              fullWidth
            />
          </Grid>

          <SubHeading size="sm">{t('profile.studies')}</SubHeading>
          {studies.length === 0 ? (
            <Text size="sm" color="secondary">
              {t('profile.noStudies')}
            </Text>
          ) : (
            studies.map((study, idx) => (
              <Grid key={study.id || idx} style={{ marginBottom: spacing[3] }}>
                <FieldRow
                  label={t('register.studies.degreeType')}
                  value={degreeTypeLabel(study.degreeType)}
                />
                <FieldRow
                  label={t('register.studies.studyTitleEn')}
                  value={
                    locale === 'gr'
                      ? study.titleGr || study.titleEn
                      : study.titleEn || study.titleGr
                  }
                />
                <FieldRow
                  label={t('register.studies.institution')}
                  value={
                    locale === 'gr'
                      ? study.institutionGr || study.institutionEn || study.institution
                      : study.institutionEn || study.institutionGr || study.institution
                  }
                  fullWidth
                />
              </Grid>
            ))
          )}
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('profile.sections.membership')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('register.membershipCategory')}
              value={membershipTypeLabel(ind.membershipType)}
            />
            <FieldRow label={t('profile.mainCommittee')} value={ind.mainCommittee} />
            <FieldRow
              label={t('profile.additionalCommittees')}
              value={additional.length ? additional.join(', ') : ''}
              fullWidth
            />
          </Grid>
        </Section>
      </>
    );
  };

  const renderCommitteeRep = (rep: InstitutionCommittee, index: number) => {
    const committeeName =
      locale === 'gr'
        ? rep.committee?.nameGr || rep.committee?.nameEn
        : rep.committee?.nameEn || rep.committee?.nameGr;
    const code = rep.committee?.code;

    return (
      <div key={rep.id || index} style={{ marginBottom: spacing[4] }}>
        <SubHeading size="sm">
          {t(`register.representative${index + 1}` as 'register.representative1')}
          {code || committeeName
            ? ` — ${[code, committeeName].filter(Boolean).join(' · ')}`
            : ''}
        </SubHeading>
        <Grid>
          <FieldRow
            label={t('register.titleOption')}
            value={titleLabel(rep.representativeTitle)}
          />
          <FieldRow label={t('petitions.applicantName')} value={bilingualName(rep.name, locale)} />
          <FieldRow
            label={t('profile.dateOfBirth')}
            value={formatDate(rep.representativeDateOfBirth)}
          />
          <FieldRow
            label={t('institution.icomRepresentativePosition')}
            value={rep.representativePosition}
          />
          <FieldRow label={t('institution.email')} value={rep.representativeEmail} />
          <FieldRow label={t('institution.phone')} value={rep.representativePhone} />
        </Grid>
      </div>
    );
  };

  const renderInstitutionSections = (institution: InstitutionDto) => {
    const contactEmail =
      institution.institutionEmail || detail.accountEmail || institution.directorEmail || '';
    const contactPhone = institution.institutionPhone || institution.directorPhone || '';
    const committees = institution.committees ?? [];

    return (
      <>
        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('institution.sections.institution')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('register.institutionNameEn')}
              value={institution.institutionName?.en}
            />
            <FieldRow
              label={t('register.institutionNameGr')}
              value={institution.institutionName?.gr}
            />
            <FieldRow
              label={t('institution.foundationDate')}
              value={formatDate(institution.foundationDate)}
            />
            <FieldRow label={t('institution.website')} value={institution.website} />
            <FieldRow
              label={t('institution.institutionType')}
              value={
                institution.institutionType?.length
                  ? institution.institutionType
                      .map(v => formatInstitutionTypeLabel(v, t))
                      .join(', ')
                  : undefined
              }
              fullWidth
            />
            <FieldRow
              label={t('institution.administrativeStatusLabel')}
              value={administrativeStatusLabel(institution.administrativeStatus)}
            />
            {!!institution.collectionTypes?.length && (
            <FieldRow
              label={t('institution.collectionTypes')}
              value={institution.collectionTypes.join(', ')}
              fullWidth
            />
            )}
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('institution.sections.contact')}
          </Heading>
          <Grid>
            <FieldRow label={t('institution.email')} value={contactEmail} />
            <FieldRow label={t('petitions.accountEmail')} value={detail.accountEmail} />
            <FieldRow
              label={t('institution.phone')}
              value={contactPhone}
            />
            <FieldRow
              label={t('institution.address')}
              value={formatAddress(institution.primaryAddress, locale)}
              fullWidth
            />
            <FieldRow
              label={t('register.secondaryAddress')}
              value={formatAddress(institution.secondaryAddress, locale)}
              fullWidth
            />
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('register.institutionDirector')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('register.titleOption')}
              value={titleLabel(institution.directorTitle)}
            />
            <FieldRow
              label={t('institution.director')}
              value={bilingualName(institution.directorName, locale)}
            />
            <FieldRow
              label={t('profile.dateOfBirth')}
              value={formatDate(institution.directorDateOfBirth)}
            />
            <FieldRow label={t('institution.directorEmail')} value={institution.directorEmail} />
            <FieldRow label={t('institution.directorPhone')} value={institution.directorPhone} />
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('institution.icomRepresentative')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('register.titleOption')}
              value={titleLabel(institution.icomRepresentativeTitle)}
            />
            <FieldRow
              label={t('institution.icomRepresentative')}
              value={bilingualName(institution.icomRepresentativeName, locale)}
            />
            <FieldRow
              label={t('profile.dateOfBirth')}
              value={formatDate(institution.icomRepresentativeDateOfBirth)}
            />
            <FieldRow
              label={t('institution.icomRepresentativePosition')}
              value={institution.icomRepresentativePosition}
            />
            <FieldRow
              label={t('institution.icomRepresentativeEmail')}
              value={institution.icomRepresentativeEmail}
            />
            <FieldRow
              label={t('institution.icomRepresentativePhone')}
              value={institution.icomRepresentativePhone}
            />
            <FieldRow
              label={t('register.contactPreference')}
              value={icomContactPrefLabel(institution.icomContactPreference)}
            />
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('institution.sections.membership')}
          </Heading>
          <Grid>
            <FieldRow
              label={t('institution.membershipCategory')}
              value={membershipCategoryLabel(institution.membershipCategory)}
            />
            <FieldRow
              label={t('institution.numberOfMemberCards')}
              value={institution.numberOfMemberCards}
            />
            <FieldRow
              label={t('institution.registrationYear')}
              value={institution.registrationYear}
            />
          </Grid>
        </Section>

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('institution.sections.committees')}
          </Heading>
          {committees.length === 0 ? (
            <Text size="sm" color="secondary">
              {t('institution.noCommittees')}
            </Text>
          ) : (
            committees
              .slice()
              .sort((a, b) => (a.voteNumber ?? 0) - (b.voteNumber ?? 0))
              .map((rep, idx) => renderCommitteeRep(rep, idx))
          )}
        </Section>
      </>
    );
  };

  return (
    <Page>
      <Stack direction="column" spacing="lg">
        <PageHeader
          title={t('petitions.reviewTitle')}
          subtitle={detail.applicationReferenceNumber}
          pattern
          actions={
            <Button variant="outline" onClick={() => navigate('/admin/petitions')}>
              {t('common.back')}
            </Button>
          }
        />

        <Section variant="elevated">
          <Flex direction="row" justify="space-between" align="center" wrap="wrap" gap="md">
            <div>
              <Heading variant="h3" as="h2" style={{ margin: 0 }}>
                {displayName || t('petitions.applicantName')}
              </Heading>
              <Text size="sm" color="secondary" style={{ marginTop: spacing[2] }}>
                {detail.type === 'Individual'
                  ? t('petitions.individual')
                  : t('petitions.institution')}
              </Text>
            </div>
            <StatusBadge $status={statusNum}>{statusLabel(statusNum)}</StatusBadge>
          </Flex>

          <Grid style={{ marginTop: spacing[5] }}>
            <Field>
              <FieldLabel>{t('petitions.reference')}</FieldLabel>
              <FieldValue>{detail.applicationReferenceNumber}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>{t('petitions.submittedAt')}</FieldLabel>
              <FieldValue>{formatAthensDateTime(detail.submittedAt, i18n.language)}</FieldValue>
            </Field>
            {detail.reviewedAt && (
              <Field>
                <FieldLabel>{t('petitions.reviewedAt')}</FieldLabel>
                <FieldValue>{formatAthensDateTime(detail.reviewedAt, i18n.language)}</FieldValue>
              </Field>
            )}
          </Grid>

          {detail.adminFeedback && (
            <FeedbackBox style={{ marginTop: spacing[5] }}>
              <Label>{t('petitions.adminFeedback')}</Label>
              <Text size="sm" style={{ marginTop: spacing[2] }}>
                {detail.adminFeedback}
              </Text>
            </FeedbackBox>
          )}
        </Section>

        {ind && renderIndividualSections()}
        {inst && renderInstitutionSections(inst)}

        <Section variant="elevated">
          <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
            {t('petitions.sectionUploads')}
          </Heading>
          {detail.documents?.length ? (
            detail.documents.map(doc => (
              <DocRow key={doc.id}>
                <div>
                  <Text size="sm">{doc.originalFileName}</Text>
                  <Text size="xs" color="secondary">
                    {documentTypeLabel(doc.documentType, t)} ·{' '}
                    {formatAthensDateTime(doc.uploadedAt, i18n.language)}
                  </Text>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(doc.id, doc.originalFileName)}
                >
                  <ArrowDownTrayIcon width={16} height={16} aria-hidden />
                  {t('common.download')}
                </Button>
              </DocRow>
            ))
          ) : (
            <Text size="sm" color="secondary">
              {t('petitions.noUploads')}
            </Text>
          )}
        </Section>

        {canReview && (
          <Section variant="elevated">
            <Heading variant="h4" as="h3" style={{ marginBottom: spacing[4] }}>
              {t('petitions.reviewActions')}
            </Heading>
            <Flex direction="row" gap="md" wrap="wrap">
              <Button
                variant="primary"
                disabled={actionLoading}
                onClick={() => setConfirmApprove(true)}
              >
                {t('petitions.approve')}
              </Button>
              <Button
                variant="outline"
                disabled={actionLoading}
                onClick={() => {
                  setReviewMessage('');
                  setReviewModal('needsChanges');
                }}
              >
                {t('petitions.requestChanges')}
              </Button>
              <Button
                variant="outline"
                disabled={actionLoading}
                onClick={() => {
                  setReviewMessage('');
                  setReviewModal('reject');
                }}
              >
                {t('petitions.reject')}
              </Button>
            </Flex>
          </Section>
        )}
      </Stack>

      <Modal
        isOpen={reviewModal !== null}
        onClose={() => !actionLoading && setReviewModal(null)}
        title={
          reviewModal === 'needsChanges'
            ? t('petitions.requestChangesTitle')
            : t('petitions.rejectTitle')
        }
        footer={
          <Flex direction="row" gap="sm" justify="end">
            <Button variant="outline" disabled={actionLoading} onClick={() => setReviewModal(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={actionLoading}
              disabled={!reviewMessage.trim()}
              onClick={() =>
                runReview(
                  reviewModal === 'needsChanges'
                    ? RegistrationReviewStatus.NeedsChanges
                    : RegistrationReviewStatus.Rejected,
                  reviewMessage
                )
              }
            >
              {reviewModal === 'needsChanges'
                ? t('petitions.requestChanges')
                : t('petitions.rejectConfirm')}
            </Button>
          </Flex>
        }
      >
        <Label htmlFor="review-message">
          {reviewModal === 'needsChanges'
            ? t('petitions.changesMessage')
            : t('petitions.rejectionReason')}
        </Label>
        <TextArea
          id="review-message"
          value={reviewMessage}
          onChange={e => setReviewMessage(e.target.value)}
          placeholder={
            reviewModal === 'needsChanges'
              ? t('petitions.changesMessagePlaceholder')
              : t('petitions.rejectionReasonPlaceholder')
          }
        />
      </Modal>

      <ConfirmDialog
        isOpen={confirmApprove}
        onClose={() => {
          if (!actionLoading) setConfirmApprove(false);
        }}
        onConfirm={() => {
          void runReview(RegistrationReviewStatus.Approved);
        }}
        title={t('petitions.approveConfirmTitle', { defaultValue: 'Approve petition?' })}
        message={t('petitions.approveConfirmNamed', {
          name: displayName || detail.accountEmail || '—',
          reference: detail.applicationReferenceNumber,
          type:
            detail.type === 'Institution' ? t('petitions.institution') : t('petitions.individual'),
          defaultValue:
            'Approve {{type}} application {{reference}} for {{name}}? This activates the account. It does not create a duplicate member or institution, and it does not generate annual dues. Dues are created separately from Payments.',
        })}
        confirmText={t('petitions.approve')}
        cancelText={t('common.cancel')}
        variant="primary"
        loading={actionLoading}
        closeOnConfirm={false}
      />
    </Page>
  );
};

export default AdminRegistrationPetitionDetail;
